import React, { useState } from "react";
import { api, apiError } from "../lib/api";
import { PublicPage } from "../components/PublicPage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Flag, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

// value = canonical category (matches admin priority map); label = friendly text
const REASONS = [
  { value: "Copyright / DMCA", label: "Copyright / DMCA Violation" },
  { value: "Impersonation or Identity Theft", label: "Impersonation or Identity Theft" },
  { value: "Abuse & Harassment", label: "Harassment or Bullying" },
  { value: "Inappropriate Content", label: "Inappropriate Content" },
  { value: "Scams & Spam", label: "Spam or Scam" },
  { value: "Technical Bug", label: "Technical Bug" },
];

export default function ReportPage() {
  const [form, setForm] = useState({ category: "", reporter_name: "", reporter_email: "", reported_username: "", content_url: "", details: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isDmca = form.category === "Copyright / DMCA";

  const submit = async () => {
    if (!form.category) { toast.error("Please choose a reason for your report."); return; }
    if (!form.details.trim() && !form.content_url.trim()) { toast.error("Add a link or describe the issue."); return; }
    if (isDmca && (!form.content_url.trim() || !form.reporter_email.trim())) { toast.error("For a DMCA claim, your email and the infringing URL are required."); return; }
    setSubmitting(true);
    try {
      await api.post("/report/public", form);
      setDone(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <PublicPage title="Report Received" testid="report-page">
        <div className="flex flex-col items-center text-center py-6" data-testid="report-success">
          <CheckCircle2 className="w-14 h-14 text-green-700 mb-3" />
          <p className="text-lg font-heading font-bold text-spotter-maroon">Thank you — your report has been filed.</p>
          <p className="text-sm">Our team has been alerted and will review it as soon as possible. Its status is now <b>Pending Review</b>.</p>
        </div>
      </PublicPage>
    );
  }

  return (
    <PublicPage title="Report a Problem / DMCA" testid="report-page">
      <p>Use this form to report content that violates our rules, or to submit a copyright (DMCA) takedown request. You don't need an account to file a report.</p>

      <div className="space-y-4 mt-4">
        <div>
          <Label>Reason for Report <span className="text-red-700">*</span></Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="report-reason" className="border-2 border-black bg-white"><SelectValue placeholder="Choose a reason…" /></SelectTrigger>
            <SelectContent>{REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {isDmca && (
          <div className="border-2 border-red-600 bg-red-50 rounded-xl p-3" data-testid="dmca-section">
            <p className="flex items-center gap-2 font-heading font-bold text-red-700 text-sm mb-2"><ShieldAlert className="w-4 h-4" />Copyright (DMCA) Takedown</p>
            <p className="text-xs text-spotter-maroon mb-3">As the copyright holder (or their agent), provide your contact details and the exact link to the infringing content. Filing knowingly false claims may carry legal liability.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Your full name</Label><Input data-testid="dmca-name" value={form.reporter_name} onChange={set("reporter_name")} className="border-2 border-black bg-white" /></div>
              <div><Label>Your email <span className="text-red-700">*</span></Label><Input data-testid="dmca-email" type="email" value={form.reporter_email} onChange={set("reporter_email")} className="border-2 border-black bg-white" /></div>
            </div>
          </div>
        )}

        {!isDmca && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Your name (optional)</Label><Input data-testid="report-name" value={form.reporter_name} onChange={set("reporter_name")} className="border-2 border-black bg-white" /></div>
            <div><Label>Your email (optional)</Label><Input data-testid="report-email" type="email" value={form.reporter_email} onChange={set("reporter_email")} className="border-2 border-black bg-white" /></div>
          </div>
        )}

        <div><Label>Reported member's username (if known)</Label><Input data-testid="report-username" value={form.reported_username} onChange={set("reported_username")} placeholder="@username" className="border-2 border-black bg-white" /></div>
        <div><Label>Link to the content {isDmca && <span className="text-red-700">*</span>}</Label><Input data-testid="report-url" value={form.content_url} onChange={set("content_url")} placeholder="https://thetopspotter.com/…" className="border-2 border-black bg-white" /></div>
        <div><Label>Details</Label><Textarea data-testid="report-details-text" value={form.details} onChange={set("details")} placeholder="Describe what's wrong and any context that will help us." className="border-2 border-black bg-white min-h-28" /></div>

        <Button data-testid="report-public-submit" onClick={submit} disabled={submitting} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2 w-full sm:w-auto">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />}Submit report
        </Button>
      </div>
    </PublicPage>
  );
}
